import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Lock, Loader2, Eye, EyeOff } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "obra_admin_token";

export function useAdminAuth() {
  const [authenticated, setAuthenticated] = useState(null);

  useEffect(() => {
    const token = sessionStorage.getItem(TOKEN_KEY);
    if (!token) { setAuthenticated(false); return; }
    axios.get(`${API}/admin/check`, { headers: { Authorization: `Bearer ${token}` } })
      .then(() => setAuthenticated(true))
      .catch(() => { sessionStorage.removeItem(TOKEN_KEY); setAuthenticated(false); });
  }, []);

  const login = async (password) => {
    const res = await axios.post(`${API}/admin/verify`, { password });
    sessionStorage.setItem(TOKEN_KEY, res.data.token);
    setAuthenticated(true);
  };

  const logout = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setAuthenticated(false);
  };

  return { authenticated, login, logout };
}

export function getAdminToken() {
  return sessionStorage.getItem(TOKEN_KEY);
}

export default function AdminGate({ children }) {
  const { authenticated, login } = useAdminAuth();
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password.trim()) return;
    setLoading(true);
    setError("");
    try {
      await login(password);
    } catch {
      setError("Incorrect password");
      setPassword("");
    } finally {
      setLoading(false);
    }
  };

  if (authenticated === null) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#D4C5B0] animate-spin" />
      </div>
    );
  }

  if (authenticated) return children;

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6" data-testid="admin-gate">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-sm"
      >
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-[#0E0E0E] border border-[#1A1A1A] rounded-2xl flex items-center justify-center mx-auto mb-5">
            <Lock className="w-7 h-7 text-[#D4C5B0]" />
          </div>
          <h1 className="text-2xl font-bold text-[#EDEDED]">Admin Access</h1>
          <p className="text-[#555] text-sm mt-2">Enter your password to continue</p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="relative mb-4">
            <input
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              autoFocus
              data-testid="admin-password-input"
              className="w-full bg-[#0E0E0E] border border-[#262626] text-[#EDEDED] px-4 py-3.5 pr-12 rounded-xl text-sm focus:border-[#D4C5B0] focus:outline-none transition-colors"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#555] hover:text-[#EDEDED] transition-colors"
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>

          {error && (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-red-400 text-sm text-center mb-4"
              data-testid="admin-gate-error"
            >
              {error}
            </motion.p>
          )}

          <button
            type="submit"
            disabled={loading || !password.trim()}
            data-testid="admin-gate-submit"
            className="w-full bg-[#D4C5B0] text-[#050505] py-3.5 rounded-xl text-sm font-semibold hover:bg-[#c4b5a0] disabled:opacity-40 transition-all flex items-center justify-center gap-2"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : null}
            {loading ? "Verifying..." : "Enter"}
          </button>
        </form>
      </motion.div>
    </div>
  );
}
